import {
  Icon,
  InputContainer,
  InputIcon,
  InputText,
} from "@sellar-io/components";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { NEW_VENUE } from "../../../constants/routes";
import { useBusinessList } from "../../../hooks/api/v2/business/useBusinessList";
import { useEvents, useSellarUser } from "../../../hooks";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import {
  SellarContextActionEnum,
  useSellarContext,
} from "../../../hooks/useSellarContext.shared";
import { Mixpanel } from "../../../utils/mixpanel";
import {
  NavigationOverlayBody,
  NavigationOverlayGroup,
  NavigationOverlayHeader,
  NavigationOverlayFooter,
} from "./NavigationOverlayLayout";
import { NavigationOverlayRow } from "./NavigationOverlayRow";
import { useNavigationOverlayPresentation } from "./NavigationOverlayContext.shared";

const BUSINESS_SEARCH_THRESHOLD = 5;

export const SwitchBusinessNavigationContent = ({
  onBack,
  onClose,
}: {
  onBack?: () => void;
  onClose: () => void;
}) => {
  const [searchAvailable, setSearchAvailable] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [pendingId, setPendingId] = useState<number>();
  const [switchError, setSwitchError] = useState(false);
  const { user, changeBusiness } = useSellarUser();
  const { business: currentBusiness } = useSellarCurrentBusiness();
  const [, dispatch] = useSellarContext();
  const { track } = useEvents();
  const isPanel = useNavigationOverlayPresentation() === "panel";
  const query = useBusinessList(
    {
      search: debouncedSearch.trim(),
      postcodeSearch: true,
      query: [{ type: "hasMembership", modifier: "EQUALS", value: true }],
      sort: [{ type: "name", direction: "asc" }],
      limit: 20,
    },
    { enabled: Boolean(user) },
  );
  const businesses = query.data?.pages.flat() ?? [];
  if (!searchAvailable && businesses.length > BUSINESS_SEARCH_THRESHOLD) {
    setSearchAvailable(true);
  }
  const selectBusiness = async (business: (typeof businesses)[number]) => {
    if (pendingId !== undefined) {
      return;
    }
    if (business.id === currentBusiness?.id) {
      onClose();
      return;
    }
    setPendingId(business.id);
    setSwitchError(false);
    try {
      const switching = changeBusiness(business.id);
      track("Account Switched", {
        toBusinessId: business.id,
        toBusinessName: business.name,
        fromBusinessId: currentBusiness?.id,
        fromBusinessName: currentBusiness?.name,
      });
      if (currentBusiness?.id) {
        Mixpanel.groups.resetGroup(currentBusiness.id);
      }
      Mixpanel.groups.setGroup(business.id);
      dispatch({
        type: SellarContextActionEnum.CHANGE_SELECTED_BUSINESS_ID,
        selectedBusinessId: business.id,
      });
      await switching;
      onClose();
    } catch {
      setSwitchError(true);
    } finally {
      setPendingId(undefined);
    }
  };
  return (
    <>
      <NavigationOverlayHeader
        title="Switch business"
        autoFocusBack
        onBack={onBack}
        onClose={isPanel ? onClose : undefined}
      />
      <NavigationOverlayBody className="gap-3">
        {searchAvailable && (
          <InputContainer className="min-h-11 shrink-0">
            <InputIcon icon="MAGNIFY" />
            <InputText
              aria-label="Search your businesses"
              placeholder="Search your businesses"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </InputContainer>
        )}
        {switchError && (
          <p role="alert" className="text-sm text-red-700">
            Unable to switch business. Please try again.
          </p>
        )}
        {query.isLoading && (
          <p role="status" className="py-4 text-sm text-zinc-500">
            Loading your businesses…
          </p>
        )}
        {query.isError && (
          <div role="alert" className="text-sm text-red-700">
            <p>Unable to load your businesses.</p>
            <button
              type="button"
              className="mt-2 underline"
              onClick={() => {
                query.refetch();
              }}
            >
              Try again
            </button>
          </div>
        )}
        {!query.isLoading && (
          <NavigationOverlayGroup
            title={isPanel ? "Your businesses" : undefined}
          >
            {businesses.map((business) => {
              const selected = business.id === currentBusiness?.id;
              let description = business.DeliveryAddress.postalCode;
              if (selected && currentBusiness?.registeredCompanyName) {
                description = currentBusiness.registeredCompanyName;
              }
              if (pendingId === business.id) {
                description = "Switching…";
              }
              return (
                <NavigationOverlayRow
                  key={business.id}
                  variant="identity"
                  selected={selected}
                  disabled={pendingId !== undefined}
                  pending={pendingId === business.id}
                  onClick={() => {
                    selectBusiness(business);
                  }}
                  leading={
                    <span
                      className={`flex size-10 items-center justify-center rounded-full ${selected ? "bg-purple-100 text-purple-700" : "bg-zinc-100 text-zinc-800"}`}
                    >
                      <Icon
                        icon={
                          business.type === "supplier"
                            ? "FACTORY_OUTLINE"
                            : "STOREFRONT_OUTLINE"
                        }
                        size="20px"
                      />
                    </span>
                  }
                  description={description}
                  descriptionClassName="text-sm leading-5 font-normal text-zinc-500"
                  className="[&>span]:break-words"
                >
                  {business.name}
                </NavigationOverlayRow>
              );
            })}
            {businesses.length === 0 && !query.isError && (
              <p role="status" className="p-4 text-sm text-zinc-500">
                {debouncedSearch.trim()
                  ? "No matching businesses."
                  : "You do not have any business memberships."}
              </p>
            )}
            {query.hasNextPage && (
              <button
                type="button"
                disabled={query.isFetchingNextPage}
                onClick={() => {
                  query.fetchNextPage();
                }}
                className="p-3 text-sm text-purple-700 disabled:opacity-50"
              >
                {query.isFetchingNextPage ? "Loading…" : "Load more businesses"}
              </button>
            )}
          </NavigationOverlayGroup>
        )}
      </NavigationOverlayBody>
      <NavigationOverlayFooter
        className={isPanel ? "mx-4 mb-4 border-0 p-0" : "pt-2"}
      >
        <NavigationOverlayGroup>
          <NavigationOverlayRow
            className="text-[#8c23c7] hover:bg-zinc-100"
            to={NEW_VENUE}
            trailing={<Icon icon="CHEVRON_RIGHT" size="16px" />}
          >
            Add another business
          </NavigationOverlayRow>
        </NavigationOverlayGroup>
      </NavigationOverlayFooter>
    </>
  );
};
